import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RackingArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { useConfirm } from '@/modules/common/components/useConfirm';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { areaFactory } from '@/modules/floorplanDesign/factories';
import { resolveRackingConnectNeighbors } from '@/modules/connections/factories/connection';

const OPTIONS: { labelTranslationKey: string; value: RackingArea['rackType'] }[] = [
  { labelTranslationKey: 'one_sided_inside', value: 'ONE_SIDED_INSIDE' },
  { labelTranslationKey: 'one_sided_alongside', value: 'ONE_SIDED_ALONGSIDE' },
  { labelTranslationKey: 'two_sided_alongside', value: 'TWO_SIDED_ALONGSIDE' },
];

const usesInsideConnectionDefaults = (rackType: RackingArea['rackType']): boolean =>
  rackType === 'ONE_SIDED_INSIDE';

const connectionDefaultsChange = (
  currentRackType: RackingArea['rackType'],
  nextRackType: RackingArea['rackType'],
): boolean =>
  usesInsideConnectionDefaults(currentRackType) !== usesInsideConnectionDefaults(nextRackType);

function RackingAreaTypeComponent() {
  const { t } = useTranslation();
  const selectedAreas = useReadSelectedAreas();
  const isConfirmingRef = useRef(false);
  const { confirm, Dialog } = useConfirm();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);

  const selectedRackingArea = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return undefined;

    const area = areas[0];
    if (!isRackingArea(area)) return undefined;

    return area;
  }, [selectedAreas]);

  const onChange = useCallback(
    async (newValue: RackingArea['rackType']) => {
      if (!selectedRackingArea) return;
      if (selectedRackingArea.rackType === newValue) return;
      if (isConfirmingRef.current) return;

      const relatedConnections = useFloorplanDesignStore
        .getState()
        .interAreaConnectionActions.getRelatedTo([selectedRackingArea.id]);

      const shouldAskAboutConnections =
        relatedConnections.length > 0 &&
        connectionDefaultsChange(selectedRackingArea.rackType, newValue);

      let shouldUpdateConnections = false;

      if (shouldAskAboutConnections) {
        isConfirmingRef.current = true;

        try {
          shouldUpdateConnections = Boolean(
            await confirm({
              title: t(
                'interface:properties.area.rack.racking_type.confirm.title',
                'Update connections?',
              ),
              message: t(
                'interface:properties.area.rack.racking_type.confirm.message',
                'This rack type uses different default connection types. Do you want to update the existing connections? The rack type itself will be changed regardless.',
              ),
            }),
          );
        } finally {
          isConfirmingRef.current = false;
        }
      }

      const connectNeighbors = resolveRackingConnectNeighbors(
        newValue,
        fddmSvc.getPreference('rackingArea.connectNeighbors'),
      );

      const updatedArea: RackingArea = {
        ...selectedRackingArea,
        rackType: newValue,
        connectNeighbors,
        stationIdGenerator:
          newValue === 'TWO_SIDED_ALONGSIDE'
            ? areaFactory.buildTwoSidedRackingStationIdGenerator(
                selectedRackingArea.name,
                selectedRackingArea.rackSpec,
              )
            : areaFactory.buildOneSidedRackingStationIdGenerator(
                selectedRackingArea.name,
                selectedRackingArea.rackSpec,
              ),
        alongsideExtension: areaFactory.buildRackAlongsideExtension(newValue),
        insideExtension: areaFactory.buildRackInsideExtension(newValue),
      };

      const implicitConnections = relatedConnections.filter(
        (connection) => !connection.createdExplicitly,
      );

      historySvc.batch(() => {
        if (shouldAskAboutConnections && shouldUpdateConnections) {
          fddmSvc.deleteInterAreaConnections(relatedConnections);
        }

        if (
          shouldAskAboutConnections &&
          !shouldUpdateConnections &&
          implicitConnections.length > 0
        ) {
          /*
           * Mark these connections as explicit before updating the area.
           * This prevents updateArea from automatically switching their connection types.
           */
          fddmSvc.updateInterAreaConnections(implicitConnections);
        }

        fddmSvc.setPreference('rackingArea.rackType', newValue);
        fddmSvc.updateArea(updatedArea);
      });
    },
    [confirm, fddmSvc, historySvc, selectedRackingArea, t],
  );

  const options = useMemo(() => {
    const labels = t('interface:properties.area.rack.racking_type.options', {
      returnObjects: true,
    });

    return OPTIONS.map((item) => ({
      label: labels[item.labelTranslationKey],
      value: item.value,
    }));
  }, [t]);

  if (!selectedRackingArea) return null;

  return (
    <>
      <SelectInput options={options} onChange={onChange} value={selectedRackingArea.rackType} />

      <Dialog negativeBtnTextCustom={t('common:no', 'No')} />
    </>
  );
}

export const RackingAreaType = memo(RackingAreaTypeComponent);

